// src/features/coupon/services/adminCouponService.ts

import api from '../../../api/axios';

import {
  ADMIN_COUPON_ENDPOINTS,
} from '../../../api/endpoints/couponEndpoints';

import type {
  AdminUserCoupon,
  Coupon,
  CouponCreateRequest,
  CouponIssueRequest,
} from '../types/coupon.admin.types';


export const adminCouponService = {

  /**
   * =========================================================
   * 전체 쿠폰 목록 조회
   *
   * GET
   * /api/v2/admin/coupons
   * =========================================================
   */
  async getCoupons():
    Promise<Coupon[]> {

    const response =
      await api.get<Coupon[]>(
        ADMIN_COUPON_ENDPOINTS
          .LIST,
      );


    return response.data;
  },


  /**
   * =========================================================
   * 활성 쿠폰 목록 조회
   *
   * GET
   * /api/v2/admin/coupons/active
   * =========================================================
   */
  async getActiveCoupons():
    Promise<Coupon[]> {

    const response =
      await api.get<Coupon[]>(
        ADMIN_COUPON_ENDPOINTS
          .ACTIVE,
      );


    return response.data;
  },


  /**
   * =========================================================
   * 쿠폰 단건 조회
   *
   * GET
   * /api/v2/admin/coupons/{couponId}
   * =========================================================
   */
  async getCoupon(
    couponId: number,
  ): Promise<Coupon> {

    const response =
      await api.get<Coupon>(
        ADMIN_COUPON_ENDPOINTS
          .DETAIL(
            couponId,
          ),
      );


    return response.data;
  },


  /**
   * =========================================================
   * 쿠폰 생성
   *
   * POST
   * /api/v2/admin/coupons
   *
   * FIXED / RANDOM 모두 지원
   * =========================================================
   */
  async createCoupon(
    request:
      CouponCreateRequest,
  ): Promise<Coupon> {

    const response =
      await api.post<Coupon>(
        ADMIN_COUPON_ENDPOINTS
          .CREATE,

        request,
      );


    return response.data;
  },


  /**
   * =========================================================
   * 쿠폰 활성화
   *
   * PATCH
   * /api/v2/admin/coupons/{couponId}/activate
   * =========================================================
   */
  async activateCoupon(
    couponId: number,
  ): Promise<Coupon> {

    const response =
      await api.patch<Coupon>(
        ADMIN_COUPON_ENDPOINTS
          .ACTIVATE(
            couponId,
          ),
      );


    return response.data;
  },


  /**
   * =========================================================
   * 쿠폰 비활성화
   *
   * PATCH
   * /api/v2/admin/coupons/{couponId}/deactivate
   * =========================================================
   */
  async deactivateCoupon(
    couponId: number,
  ): Promise<Coupon> {

    const response =
      await api.patch<Coupon>(
        ADMIN_COUPON_ENDPOINTS
          .DEACTIVATE(
            couponId,
          ),
      );


    return response.data;
  },


  /**
   * =========================================================
   * 회원에게 쿠폰 발급
   *
   * POST
   * /api/v2/admin/users/{userId}/coupons/{couponId}
   *
   * userId:
   * 회원 PK가 아니라 로그인 ID 문자열
   * =========================================================
   */
  async issueCoupon(
    userId: string,
    couponId: number,
    request?:
      CouponIssueRequest,
  ): Promise<AdminUserCoupon> {

    const response =
      await api.post<
        AdminUserCoupon
      >(
        ADMIN_COUPON_ENDPOINTS
          .ISSUE(
            userId,
            couponId,
          ),

        request ?? {},
      );


    return response.data;
  },


  /**
   * =========================================================
   * 특정 회원 전체 쿠폰 조회
   *
   * GET
   * /api/v2/admin/users/{userId}/coupons
   * =========================================================
   */
  async getUserCoupons(
    userId: string,
  ): Promise<
    AdminUserCoupon[]
  > {

    const response =
      await api.get<
        AdminUserCoupon[]
      >(
        ADMIN_COUPON_ENDPOINTS
          .USER_COUPONS(
            userId,
          ),
      );


    return response.data;
  },


  /**
   * =========================================================
   * 발급된 사용자 쿠폰 단건 조회
   *
   * GET
   * /api/v2/admin/user-coupons/{userCouponId}
   * =========================================================
   */
  async getUserCoupon(
    userCouponId: number,
  ): Promise<
    AdminUserCoupon
  > {

    const response =
      await api.get<
        AdminUserCoupon
      >(
        ADMIN_COUPON_ENDPOINTS
          .USER_COUPON_DETAIL(
            userCouponId,
          ),
      );


    return response.data;
  },


  /**
   * =========================================================
   * 관리자 쿠폰 사용 처리
   *
   * PATCH
   * /api/v2/admin/user-coupons/{userCouponId}/use
   *
   * FIXED
   * -> 지정된 보상 지급
   *
   * RANDOM
   * -> 확률 추첨
   * -> selectedReward* 저장
   * -> 실제 보상 지급
   * =========================================================
   */
  async useUserCoupon(
    userCouponId: number,
  ): Promise<
    AdminUserCoupon
  > {

    const response =
      await api.patch<
        AdminUserCoupon
      >(
        ADMIN_COUPON_ENDPOINTS
          .USE_USER_COUPON(
            userCouponId,
          ),
      );


    return response.data;
  },


  /**
   * =========================================================
   * 발급된 쿠폰 취소
   *
   * PATCH
   * /api/v2/admin/user-coupons/{userCouponId}/cancel
   *
   * AVAILABLE 쿠폰만 취소 가능
   * =========================================================
   */
  async cancelUserCoupon(
    userCouponId: number,
  ): Promise<
    AdminUserCoupon
  > {

    const response =
      await api.patch<
        AdminUserCoupon
      >(
        ADMIN_COUPON_ENDPOINTS
          .CANCEL_USER_COUPON(
            userCouponId,
          ),
      );


    return response.data;
  },

};